<template>
  <div class="bookings-list-tab">
    <div class="filters d-flex flex-wrap align-items-start py-3" style="gap: 0.75rem 1rem;">
      <b-form-group label="Employees" class="mb-0">
        <MultiSelectChips
          v-model="selectedEmployees"
          :items="employeeItems"
          placeholder="Search by name or email..."
          @search="employeeSearch"
          @nextPage="employeeNextPage"
        />
      </b-form-group>
      <b-form-group label="Floors" class="mb-0">
        <MultiSelectChips v-model="selectedFloors" :items="floorItems" placeholder="Search floors..." @search="floorSearch" />
      </b-form-group>
      <b-form-group label="From" class="mb-0">
        <AppDateSelect :date="dateFrom" :min-date="null" @change="setDate('from', $event)" />
      </b-form-group>
      <b-form-group label="To" class="mb-0">
        <AppDateSelect :date="dateTo" :min-date="dateFrom" @change="setDate('to', $event)" />
      </b-form-group>
      <div class="ml-auto text-muted small pt-4">
        <span v-if="loading">Loading...</span>
        <span v-else>{{ pager.totalCount }} booking{{ pager.totalCount === 1 ? '' : 's' }} found</span>
      </div>
    </div>

    <b-card no-body>
      <div class="table-responsive" v-if="rows.length">
        <b-table class="card-table" :items="rows" :fields="fields">
          <template #cell(employee)="data">
            <div class="d-flex align-items-center">
              <app-avatar :src="avatarSrc(data.item.avatarId)" class="mr-2" />
              {{ data.item.employeeName }}
            </div>
          </template>
          <template #cell(desk)="data">
            {{ data.item.deskLabel }}
            <!-- only while the booking still stands: the map shows the desk as it is now, and
                 a finished booking's desk may since have been taken by someone else -->
            <router-link
              v-if="data.item.pointId && !data.item.isPast"
              :to="deskMapLink(data.item)"
              target="_blank"
              title="View on map"
              class="ml-1"
            >
              <i class="ion ion-md-locate"></i>
            </router-link>
          </template>
          <template #cell(date)="data">
            {{ data.item.dateLabel }}
            <div v-if="data.item.floorDateLabel" class="text-muted small">{{ data.item.floorDateLabel }}</div>
          </template>
          <template #cell(time)="data">
            <span v-if="!data.item.timeLabel" class="text-muted">&mdash;</span>
            <template v-else>
              {{ data.item.timeLabel }}
              <div v-if="data.item.floorTimeLabel" class="text-muted small">{{ data.item.floorTimeLabel }}</div>
            </template>
          </template>
          <template #cell(status)="data">
            <b-badge v-if="data.item.badge" :variant="data.item.badge.variant">
              <i v-if="data.item.badge.icon" class="ion" :class="data.item.badge.icon" />
              {{ data.item.badge.text }}
            </b-badge>
          </template>
        </b-table>
      </div>
      <div v-else-if="loadFailed" class="text-center m-4 text-big text-muted">Could not load bookings.</div>
      <div v-else-if="!loading" class="text-center m-4 text-big">No bookings match these filters.</div>

      <b-pagination
        v-if="pager.pageCount > 1"
        class="m-3"
        v-model="page"
        :total-rows="pager.totalCount"
        :per-page="pager.perPage"
      />
    </b-card>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions } from 'vuex'
import AppAvatar from '@/components/shared/AppAvatar'
import AppDateSelect from '@/components/shared/AppDateSelect'
import MultiSelectChips from '@/components/bookings/MultiSelectChips.vue'
import {
  bookingsQueryParams,
  endOfDay,
  filtersFromQuery,
  filtersToQuery,
  floorOptions,
  toBookingRow
} from '@/components/bookings/utils'
import { getAvatarSrc } from '@/utils/Helpers'
import { getIconUrl } from '@/components/leaflet/Icon'
import { POINT_ICONS } from '@/store/modules/point'

const EMPLOYEE_PAGE_SIZE = 8
const PER_PAGE = 20
// the filters are three controls that often move together; the same debounce the desk
// editor uses for its busy-map lookups
const FILTER_DEBOUNCE_MS = 600

export default {
  name: 'BookingsListTab',
  components: { AppAvatar, AppDateSelect, MultiSelectChips },
  data() {
    const today = dayjs().startOf('day')
    // a card on /home links here with the period and the floors it was reporting on
    const opened = filtersFromQuery(this.$route.query)
    const floors = this.floorOptionsNow()
    const selectedFloors = floors.filter((floor) => opened.floors.includes(floor.id))

    return {
      selectedEmployees: [],
      // ids without names yet: the link carries who, the server has to say who that is
      pendingEmployeeIds: opened.employees,
      // the table says "loading" rather than "nothing found" while that is on its way
      loading: opened.employees.length > 0,
      selectedFloors,
      // floors are loaded at boot, so they are normally here already; ids left over are
      // adopted by the watcher when the store catches up
      pendingFloorIds: selectedFloors.length ? [] : opened.floors,
      // the pickers name whole days: From opens one, To closes one - see endOfDay()
      dateFrom: opened.since ? opened.since.startOf('day') : today,
      dateTo: opened.until ? endOfDay(opened.until) : endOfDay(today.add(30, 'day')),
      // whether the period is the reader's, and so belongs in the address - see filtersToQuery()
      periodChosen: Boolean(opened.since || opened.until),
      rows: [],
      pager: { currentPage: 1, perPage: PER_PAGE, pageCount: 0, totalCount: 0 },
      page: 1,
      loadFailed: false,
      // filters and pages both trigger a load, and the slower answer must not win
      requestId: 0,
      employeeItems: [],
      employeeSearchTerm: '',
      employeePage: 1,
      employeePageCount: 1,
      employeePageLoading: false,
      floorTerm: '',
      floorItems: [],
      reloadTimeoutId: null
    }
  },
  computed: {
    // floorsLoad commits to the store without returning anything, so read it reactively
    allFloors() {
      return this.floorOptionsNow()
    },
    fields() {
      return [
        { key: 'employee', label: 'Employee' },
        { key: 'desk', label: 'Desk' },
        { key: 'date', label: 'Date' },
        { key: 'time', label: 'Time' },
        { key: 'status', label: '' }
      ]
    },
    employeeIconSrc() {
      return getIconUrl(POINT_ICONS.EMPLOYEE)
    },
    filterState() {
      return {
        employees: this.selectedEmployees,
        floors: this.selectedFloors,
        from: this.dateFrom,
        to: this.dateTo
      }
    },
    // What the address should say about this tab. Ids still waiting on their names count:
    // they are the reader's filter, only not on screen yet.
    addressQuery() {
      return filtersToQuery({
        employees: this.selectedEmployees.length ? this.selectedEmployees.map((item) => item.id) : this.pendingEmployeeIds,
        floors: this.selectedFloors.length ? this.selectedFloors.map((item) => item.id) : this.pendingFloorIds,
        since: this.periodChosen ? this.dateFrom : null,
        until: this.periodChosen ? this.dateTo : null
      })
    }
  },
  methods: {
    ...mapActions('desk', ['deskEventsGroupedSearch']),
    // data() runs before the computeds exist and it needs the floors the link named, so the
    // store is read here rather than through allFloors
    floorOptionsNow() {
      return floorOptions(this.$store.getters['floor/floors'] || [], () => null)
    },
    ...mapActions('employee', ['employeesSearch']),
    ...mapActions('floor', ['floorsLoad']),
    async load() {
      const requestId = ++this.requestId
      this.loading = true
      try {
        const response = await this.deskEventsGroupedSearch({
          params: bookingsQueryParams({ ...this.filterState, page: this.page, perPage: PER_PAGE })
        })
        if (requestId !== this.requestId) return
        this.rows = response.data.items.map((row) => toBookingRow(row))
        this.pager = response.data.pager
        this.loadFailed = false
      } catch (e) {
        // Api already toasts the failure; here the table only has to stop showing a result
        if (requestId !== this.requestId) return
        this.rows = []
        this.pager = { currentPage: 1, perPage: PER_PAGE, pageCount: 0, totalCount: 0 }
        this.loadFailed = true
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    reloadSoon() {
      clearTimeout(this.reloadTimeoutId)
      this.reloadTimeoutId = setTimeout(() => {
        this.reloadTimeoutId = null
        this.load()
      }, FILTER_DEBOUNCE_MS)
    },
    // Only To is bounded (by From), so moving From forward could leave an inverted window,
    // which the server answers with a 400 - the table would just go blank. Carry To along
    // instead, keeping the span the user had set, the same way the Free Desks window does.
    setDate(field, date) {
      const bound = field === 'from' ? 'dateFrom' : 'dateTo'
      const value = field === 'from' ? date.startOf('day') : endOfDay(date)
      // re-picking the day already in the field is not a filter change
      if (value.isSame(this[bound])) return

      // whole days, so To on From's own day is a full one-day window, not an empty one
      const days = Math.max(this.dateTo.diff(this.dateFrom, 'day'), 0)
      this.periodChosen = true
      this[bound] = value
      if (field === 'from' && !this.dateTo.isAfter(this.dateFrom)) {
        this.dateTo = endOfDay(this.dateFrom.add(days, 'day'))
      }
    },
    avatarSrc(avatarId) {
      return getAvatarSrc(avatarId, '', this.employeeIconSrc)
    },
    deskMapLink(row) {
      return { name: 'map', params: { floorId: row.floorId }, query: { pointId: row.pointId } }
    },
    /**
     * The employees a link named.
     *
     * Only their ids travel in the address, and the picker shows names - so they are fetched
     * before the first search rather than filled in behind it. Setting the selection is what
     * schedules that search (see the filterState watcher), which is why created() stands
     * aside when this is running.
     */
    async adoptQueryEmployees() {
      const ids = this.pendingEmployeeIds

      try {
        const response = await this.employeesSearch({ params: { id: ids, pagerDisabled: 1 } })
        const found = response.data.items.map((employee) => ({ id: employee.id, label: employee.name }))
        if (found.length) {
          this.selectedEmployees = found
          // the watcher schedules a debounced reload for that; the debounce is there to
          // coalesce typing, and nobody typed this - search at once instead
          await this.$nextTick()
          clearTimeout(this.reloadTimeoutId)
          this.reloadTimeoutId = null
        }
      } catch (e) {
        // Api toasts it; an unfiltered list is a better answer than an empty screen
      }
      // only now: until the names are in, the ids are what keeps the filter in the address
      this.pendingEmployeeIds = []

      this.load()
    },
    employeeSearch(term) {
      this.employeeSearchTerm = term
      this.employeePage = 1
      this.employeePageCount = 1
      this.employeeItems = []
      this.loadEmployeePage()
    },
    // AppSelect emits nextPage straight from its scroll handler, so one flick of the wheel
    // asks for a dozen pages - guard the same way DeskEventEditor does, and stop at the last
    employeeNextPage() {
      if (this.employeePageLoading || this.employeePage >= this.employeePageCount) return
      this.employeePage++
      this.loadEmployeePage()
    },
    async loadEmployeePage() {
      this.employeePageLoading = true
      try {
        const response = await this.employeesSearch({
          params: { term: this.employeeSearchTerm, page: this.employeePage, 'per-page': EMPLOYEE_PAGE_SIZE }
        })
        this.employeePageCount = response.data.pager.pageCount
        this.employeeItems = [
          ...this.employeeItems,
          ...response.data.items.map((employee) => ({ id: employee.id, label: employee.name }))
        ]
      } catch (e) {
        // Api toasts it; the picker just keeps what it already has and lets the user retry
        this.employeePage = Math.max(this.employeePage - 1, 1)
      } finally {
        this.employeePageLoading = false
      }
    },

    floorSearch(term) {
      this.floorTerm = term
      this.applyFloorTerm()
    },
    applyFloorTerm() {
      const needle = this.floorTerm.toLowerCase()
      this.floorItems = this.allFloors.filter((floor) => !needle || floor.label.toLowerCase().includes(needle))
    }
  },
  watch: {
    addressQuery: {
      immediate: true,
      handler(query) {
        this.$emit('query-change', query)
      }
    },
    allFloors(floors) {
      this.applyFloorTerm()

      // the floors a link named, once the store has something to name them with
      if (!this.pendingFloorIds.length) return
      const picked = floors.filter((floor) => this.pendingFloorIds.includes(floor.id))
      this.pendingFloorIds = []
      if (picked.length) this.selectedFloors = picked
    },
    filterState: {
      deep: true,
      handler() {
        // a new filter invalidates the page the user is on
        this.page = 1
        this.reloadSoon()
      }
    },
    page() {
      // a filter change resets the page itself and schedules its own reload - without this
      // the two would each fire a request
      if (this.reloadTimeoutId) return
      this.load()
    }
  },
  created() {
    this.floorsLoad()

    // the search follows the names, so that there is one request and not two
    if (this.pendingEmployeeIds.length) {
      this.adoptQueryEmployees()
      return
    }

    this.load()
  },
  beforeDestroy() {
    clearTimeout(this.reloadTimeoutId)
  }
}
</script>

<style scoped lang="scss">
.filters {
  border-bottom: 1px solid #eee;
}
</style>
